// src/theme/Navbar/Content/index.tsx
import React from 'react';
import clsx from 'clsx';
import Link from '@docusaurus/Link';
import { useThemeConfig } from '@docusaurus/theme-common';
import { splitNavbarItems, useNavbarMobileSidebar } from '@docusaurus/theme-common/internal';
import NavbarLogo from '@theme/Navbar/Logo';
import NavbarItem from '@theme/NavbarItem';
import NavbarMobileSidebarToggle from '@theme/Navbar/MobileSidebar/Toggle';
import NavbarMobileSidebar from '@theme/Navbar/MobileSidebar';
import { SidebarIcon } from '@site/src/components/icons/sidebar-icons';

type AnyItem = any;

function useNavbarItems(): AnyItem[] {
  return (useThemeConfig().navbar.items as AnyItem[]) ?? [];
}

// Regular nav link — underline slide-in on hover
function DesktopNavLink({ item }: { item: AnyItem }) {
  const icon = item.icon as string | undefined;
  const inner = (
    <span className="inline-flex items-center gap-1.5">
      {icon && <SidebarIcon name={icon} className="h-3.5 w-3.5 opacity-60" />}
      {item.label}
    </span>
  );

  const cls = clsx(
    'relative text-sm font-medium text-[var(--color-text)] hover:no-underline',
    'after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full',
    'after:bg-[var(--color-text)] after:scale-x-0 after:transition-transform after:duration-200 after:origin-left',
    'hover:after:scale-x-100'
  );

  if (item.to)
    return (
      <Link to={item.to} className={cls}>
        {inner}
      </Link>
    );
  return (
    <a href={item.href} className={cls}>
      {inner}
    </a>
  );
}

// Newsletter pill
function NewsletterLink({ item }: { item: AnyItem }) {
  const cls = clsx(
    'text-xs font-medium px-3 py-1.5 rounded-full',
    'border border-[var(--color-separator)]',
    'text-[var(--color-text-subtle)] hover:text-[var(--color-text)]',
    'bg-transparent hover:bg-[var(--color-background-subtle)]',
    'hover:no-underline transition-colors duration-150'
  );
  if (item.to)
    return (
      <Link to={item.to} className={cls}>
        {item.label}
      </Link>
    );
  return (
    <a href={item.href} className={cls}>
      {item.label}
    </a>
  );
}

// Dashboard / Sign In primary button
function DashboardButton({ item }: { item: AnyItem }) {
  const icon = item.icon as string | undefined;
  const cls = clsx(
    'inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg',
    'bg-[var(--color-black-action)] text-[var(--color-text-on-primary)]',
    'hover:opacity-90 hover:no-underline transition-opacity duration-150'
  );
  const inner = (
    <>
      {icon && <SidebarIcon name={icon} className="h-3.5 w-3.5" />}
      {item.label}
    </>
  );
  if (item.to)
    return (
      <Link to={item.to} className={cls}>
        {inner}
      </Link>
    );
  return (
    <a href={item.href} className={cls}>
      {inner}
    </a>
  );
}

function DesktopItems({ items }: { items: AnyItem[] }) {
  return (
    <div className="flex items-center gap-5">
      {items.map((item, idx) => {
        if (item.className === 'navbar-dashboard-cta') {
          return <DashboardButton key={idx} item={item} />;
        }
        if (item.className === 'navbar-newsletter-cta') {
          return <NewsletterLink key={idx} item={item} />;
        }
        // docSidebar or custom types (locale dropdown etc.)
        if (item.type && item.type !== 'default') {
          return (
            <NavbarItem
              key={idx}
              {...item}
              className="text-sm font-medium text-[var(--color-text)] hover:no-underline"
            />
          );
        }
        return <DesktopNavLink key={idx} item={item} />;
      })}
    </div>
  );
}

export default function NavbarContent() {
  const items = useNavbarItems();
  const [leftItems, rightItems] = splitNavbarItems(items);
  const mobileSidebar = useNavbarMobileSidebar();

  return (
    <nav
      aria-label="Main navigation"
      className="w-full bg-[var(--color-background)] border-b border-[var(--color-separator)] fixed top-0 left-0 z-20"
    >
      <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left: logo + left nav items */}
        <div className="flex items-center gap-8">
          <NavbarLogo />
          <div className="hidden lg:flex">
            <DesktopItems items={leftItems} />
          </div>
        </div>

        {/* Right: right nav items + mobile toggle */}
        <div className="flex items-center gap-4">
          <div className="hidden lg:flex">
            <DesktopItems items={rightItems} />
          </div>
          <div className="lg:hidden">{!mobileSidebar.disabled && <NavbarMobileSidebarToggle />}</div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className="lg:hidden">
        <NavbarMobileSidebar />
      </div>
    </nav>
  );
}
